import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { getAgencyById, getRocketsByAgency, mockScientists, mockSatellites, mockLaunches } from '../services/dataService';
import { getAgencyImage } from '../utils/imageUtils';

/**
 * Agency Details Page Component
 */
const AgencyDetails = () => {
  const { id } = useParams();
  const agency = getAgencyById(id);
  const rockets = getRocketsByAgency(id);
  const scientists = mockScientists.filter(s => s.agencyId === id);
  const satellites = mockSatellites.filter(s => s.agencyId === id);
  const launches = mockLaunches.filter(l => l.agencyId === id);

  if (!agency) {
    return (
      <div className="container" style={{ paddingTop: '50px', textAlign: 'center' }}>
        <h2>Agency not found</h2>
        <Link to="/agencies" className="btn btn-primary" style={{ marginTop: '20px' }}>Back to Agencies</Link>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '30px' }}>
      <Link to="/agencies" className="btn btn-secondary" style={{ marginBottom: '20px' }}>
        ← Back to Agencies
      </Link>

      {/* Overview Section */}
      <div className="card" style={{ marginBottom: '30px' }}>
        <img 
          src={getAgencyImage(agency.id, agency.name)} 
          alt={agency.name}
          style={{ 
            width: '100%', 
            maxWidth: '800px',
            height: '400px', 
            objectFit: 'cover', 
            borderRadius: '8px', 
            marginBottom: '20px',
            display: 'block',
            margin: '0 auto 20px'
          }}
          onError={(e) => {
            e.target.src = `https://via.placeholder.com/800x400/1a1a2e/e94560?text=${encodeURIComponent(agency.name)}`;
          }}
        />
        <h1 style={{ fontSize: '36px', color: 'var(--highlight-color)', marginBottom: '20px' }}>
          {agency.name}
        </h1>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '20px' }}>
          <div>
            <p><strong>Country:</strong> {agency.country}</p>
            <p><strong>Founded:</strong> {agency.foundedYear}</p>
            <p><strong>Founder:</strong> {agency.founder}</p>
          </div>
          <div>
            <p><strong>Headquarters:</strong> {agency.headquarters}</p>
            <p><strong>Active Rockets:</strong> {agency.activeRockets}</p>
            <p><strong>Scientists:</strong> {agency.scientistsCount}</p>
          </div>
        </div>
        <p style={{ color: 'var(--text-dark)', lineHeight: '1.8', marginTop: '20px' }}>
          {agency.description}
        </p>
      </div>

      {/* Rockets Section */}
      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '28px', marginBottom: '20px' }}>🚀 Rockets by {agency.name}</h2>
        {rockets.length > 0 ? (
          <div className="grid">
            {rockets.map(rocket => (
              <div key={rocket.id} className="card">
                <h3 style={{ color: 'var(--highlight-color)', marginBottom: '10px' }}>
                  {rocket.name}
                </h3>
                <p><strong>Type:</strong> {rocket.type}</p>
                <p><strong>Status:</strong> {rocket.status}</p>
                <Link to={`/rockets/${rocket.id}`} className="btn btn-primary" style={{ marginTop: '15px', display: 'block', textAlign: 'center' }}>
                  View Rocket
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: 'var(--text-dark)' }}>No rockets found for this agency.</p>
        )}
      </section>

      {/* Scientists Section */}
      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '28px', marginBottom: '20px' }}>👨‍🔬 Scientists by {agency.name}</h2>
        {scientists.length > 0 ? (
          <div className="grid">
            {scientists.map(scientist => (
              <div key={scientist.id} className="card">
                <h3 style={{ color: 'var(--highlight-color)', marginBottom: '10px' }}>
                  {scientist.name}
                </h3>
                <p><strong>Field:</strong> {scientist.field}</p>
                <p><strong>Country:</strong> {scientist.country}</p>
                <Link to={`/scientists/${scientist.id}`} className="btn btn-primary" style={{ marginTop: '15px', display: 'block', textAlign: 'center' }}>
                  View Profile
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: 'var(--text-dark)' }}>No scientists found for this agency.</p>
        )}
      </section>

      {/* Satellites Section */}
      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '28px', marginBottom: '20px' }}>🛰️ Satellites by {agency.name}</h2>
        {satellites.length > 0 ? (
          <div className="grid">
            {satellites.map(satellite => (
              <div key={satellite.id} className="card">
                <h3 style={{ color: 'var(--highlight-color)', marginBottom: '10px' }}>
                  {satellite.name}
                </h3>
                <p><strong>Orbit:</strong> {satellite.orbitType}</p>
                <p><strong>Purpose:</strong> {satellite.purpose}</p>
                <Link to={`/satellites/${satellite.id}`} className="btn btn-primary" style={{ marginTop: '15px', display: 'block', textAlign: 'center' }}>
                  View Satellite
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: 'var(--text-dark)' }}>No satellites found for this agency.</p>
        )}
      </section>

      {/* Launch History Section */}
      <section style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '28px', marginBottom: '20px' }}>📅 Launch History</h2>
        {launches.length > 0 ? (
          <div className="grid">
            {launches.map(launch => (
              <div key={launch.id} className="card">
                <h3 style={{ color: 'var(--highlight-color)', marginBottom: '10px' }}>
                  {launch.missionName}
                </h3>
                <p><strong>Date:</strong> {new Date(launch.launchDate).toLocaleDateString()}</p>
                <p><strong>Status:</strong> {launch.status}</p>
                <p><strong>Type:</strong> {launch.missionType}</p>
                <Link to={`/launches/${launch.id}`} className="btn btn-primary" style={{ marginTop: '15px', display: 'block', textAlign: 'center' }}>
                  View Launch
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: 'var(--text-dark)' }}>No launches found for this agency.</p>
        )}
      </section>
    </div>
  );
};

export default AgencyDetails;
